import React, { useState, useContext, useEffect } from "react";
import Card from "./Card";
import Button from "../Button/Button";
import "./Cards.css";
import "./style.css";
import { ConfigContext } from "../../App";
import { TranslateContext } from "../../App";
import textConfig from "../../configuration.json";

/**
 * @typedef {"newest"|"oldest"|"price_asc"|"price_desc"|"alpha_asc"|"alpha_desc"} CardsSectionFilterKey
 */

/**
 * Safely converts a card date field to a sortable timestamp.
 * Supports both `createdAt` and `publishDate` (ISO strings expected).
 *
 * @param {Object} card - Card content object
 * @returns {number} A numeric timestamp, or 0 if missing/invalid.
 */
function getCardTimestamp(card) {
  const raw = card?.createdAt || card?.publishDate;
  if (!raw || typeof raw !== "string") return 0;

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function CardsSection({ title, featured, cardsType, cardsContent, button, hasFilter, categoryFilter }) {
  const configuration = useContext(ConfigContext);
  const language = useContext(TranslateContext).language;
  const cardSectionClass = cardsType === "product" ? "product-card-section" : "card-section";
  const featuredCardSectionClass = cardsType === "product" ? "featured-product-cards" : "featured-cards";

  let categories = [];
  if (cardsContent && cardsContent.length > 0) {
    categories = cardsContent.map((card) => card.category?.title || undefined);
  }
  const uniqueCategories = categories.filter((category, index, self) => category && self.indexOf(category) === index);

  const allCategoriesLabel = textConfig.texts.allCategories[language];

  /** @type {[string, Function]} */
  const [selectedCategory, setSelectedCategory] = useState(allCategoriesLabel);

  /** @type {[CardsSectionFilterKey, Function]} */
  const [selectedFilter, setSelectedFilter] = useState("newest");

  useEffect(() => {
    // Keep defaults in sync when language changes (labels are localized).
    setSelectedCategory(allCategoriesLabel);
    setSelectedFilter("newest");
  }, [allCategoriesLabel]);

  let filteredCards =
    selectedCategory === allCategoriesLabel
      ? cardsContent
      : cardsContent.filter((card) => card.category?.title === selectedCategory);

  switch (selectedFilter) {
    case "price_asc":
      filteredCards = [...filteredCards].sort((a, b) => (Number(a?.price) || 0) - (Number(b?.price) || 0));
      break;
    case "price_desc":
      filteredCards = [...filteredCards].sort((a, b) => (Number(b?.price) || 0) - (Number(a?.price) || 0));
      break;
    case "alpha_asc":
      filteredCards = [...filteredCards].sort((a, b) => (a?.title || "").localeCompare(b?.title || ""));
      break;
    case "alpha_desc":
      filteredCards = [...filteredCards].sort((a, b) => (b?.title || "").localeCompare(a?.title || ""));
      break;
    case "newest":
      filteredCards = [...filteredCards].sort((a, b) => getCardTimestamp(b) - getCardTimestamp(a));
      break;
    case "oldest":
      filteredCards = [...filteredCards].sort((a, b) => getCardTimestamp(a) - getCardTimestamp(b));
      break;
    default:
      filteredCards = [...filteredCards];
      break;
  }

  return (
    <div className="card-section-container">
      {title && (
        <div className="card-section-title-container">
          <h2 className="h2-title">{title}</h2>
        </div>
      )}
      {hasFilter && (
        <div className="filter">
          <select
            className="filter-drop-down"
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <option value="newest">{textConfig.texts.newest[language]}</option>
            <option value="oldest">{textConfig.texts.oldest[language]}</option>
            {cardsType !== "blog" && (
              <>
                <option value="price_asc">
                  {textConfig.texts.priceLowToHigh[language]}
                </option>
                <option value="price_desc">
                  {textConfig.texts.priceHighToLow[language]}
                </option>
              </>
            )}
            <option value="alpha_asc">{textConfig.texts.alphabetical[language]}</option>
            <option value="alpha_desc">
              {textConfig.texts.alphabeticalReversed[language]}
            </option>
          </select>
          {categoryFilter && (
            <select
              className="filter-drop-down"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value={allCategoriesLabel}>{allCategoriesLabel}</option>
              {uniqueCategories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <div className={featured ? featuredCardSectionClass : cardSectionClass}>
        {filteredCards &&
          filteredCards.map((card, index) => {
            return <Card key={`card-${index}`} type={cardsType} content={card} configuration={configuration} />;
          })}
      </div>
      {button && button.text && (
        <div className="card-section-button-container">
          <Button
            type={button.size}
            text={button.text}
            configuration={configuration}
            url={button.link}
            openInNewTab={button.openInNewTab}
          />
        </div>
      )}
    </div>
  );
}

export default CardsSection;
